import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';


const baseUrl = process.env.REACT_APP_BASE_URL || '/api';

export const productsApi = createApi({
  reducerPath: 'productsApi',
  baseQuery: fetchBaseQuery({ baseUrl }),
  // Mbaj cache për disa minuta që Back të përdorë të njëjtat lista pa refetch
  keepUnusedDataFor: 300,
  tagTypes: ['Products', 'Subcategory', 'SearchProducts', 'BannerProducts'],
  endpoints: (builder) => ({
    // Fetch subcategory (mirrors existing /getsubcategory POST)
    getSubcategory: builder.query({
      // arg is the category id (string)
      query: (catId) => ({
        url: '/getsubcategory',
        method: 'POST',
        body: { catId }
      }),
      transformResponse: (response) =>
        // Axios responses previously used res.data.GroupCategoryData[0].linkedCategory
        // fetchBaseQuery returns the parsed body directly. Be permissive for both shapes.
        (response?.data ? response.data : response)?.GroupCategoryData?.[0]?.linkedCategory ||
        (response?.data ? response.data : response)?.linkedCategory || [],
      providesTags: (result, error, arg) => [{ type: 'Subcategory', id: arg }]
    }),

    // Fetch products list using RTK Query "infinite" style by serializing args (excluding page),
    // merging pages into one cached entry, and avoiding refetch on mount so cached results are preserved.
    getProducts: builder.infiniteQuery({
      infiniteQueryOptions: {
        initialPageParam: 0,
        getNextPageParam: (lastPage) => lastPage.nextPage,
      },
      // query: ({ queryArg, pageParam }) => ({
      //   url: '/productlists',
      //   method: 'POST',
      //   body: { ...queryArg, page: pageParam }
      // }),
      query: ({ queryArg, pageParam }) => {
        console.log("🔥 queryArg:", queryArg);
        console.log("📄 pageParam:", pageParam);

        return {
          url: "/productlists",
          method: "POST",
          body: { ...queryArg, page: pageParam }
        };
      },

      transformResponse: (response, meta, { queryArg, pageParam }) => {
        const root = response?.data ? response.data : response;
        const products = Array.isArray(root?.products) ? root.products : root?.ProductData || root?.data || [];
        const total = root?.totalCount ?? root?.total ?? root?.count ?? products.length;

        // Calculate next page
        // If we got fewer items than requested or if we reached total, there is no next page
        // Assuming per_page is passed in queryArg or we infer it. 
        // The original code used `loadedProducts >= lastPage.totalCount` in getNextPageParam, 
        // but here we are in transformResponse for a single page.
        // We can check if products.length is > 0 to determine if there might be more, 
        // but ideally we know the limit.
        // Let's assume if products.length === 0, we are done. 
        // Or better, if (pageParam + 1) * per_page < total.
        // We don't strictly know per_page here unless it's in queryArg.
        // In Products.js, per_page is 30.

        const limit = queryArg.per_page || 30;
        const currentCount = (pageParam + 1) * limit;
        // Note: pageParam is 0-indexed. 
        // If page 0: (0+1)*40 = 40 items covered. If total is 41, we need page 1.

        const nextPage = currentCount < total ? pageParam + 1 : null;

        return { products, totalCount: total, nextPage };
      },
      providesTags: ['Products']
    }),

    // Fetch search products using /scrollbox endpoint (for vendor/brand search)
    getSearchProducts: builder.infiniteQuery({
      infiniteQueryOptions: {
        initialPageParam: 0,
        getNextPageParam: (lastPage) => lastPage.nextPage,
      },
      query: ({ queryArg, pageParam }) => ({
        url: '/scrollbox',
        method: 'POST',
        body: { ...queryArg, page: pageParam }
      }),
      transformResponse: (response, meta, { queryArg, pageParam }) => {
        const root = response?.data ? response.data : response;
        const products =
          Array.isArray(root)
            ? root
            : Array.isArray(root?.products)
              ? root.products
              : Array.isArray(root?.data)
                ? root.data
                : Array.isArray(root?.ProductData)
                  ? root.ProductData
                  : [];
        const total = root?.totalCount ?? root?.total ?? root?.count ?? products.length;

        const limit = queryArg.per_page || 30;
        // /scrollbox doesn't always return totalCount. Use "page is full" heuristic.
        const nextPage = products.length === limit ? pageParam + 1 : null;

        return { products, totalCount: total, nextPage };
      },
      providesTags: ['SearchProducts']
    }),

    // Fetch banner products using /multifilter endpoint (for store/brand pages)
    getBannerProducts: builder.infiniteQuery({
      infiniteQueryOptions: {
        initialPageParam: 0,
        getNextPageParam: (lastPage) => lastPage.nextPage,
      },
      query: ({ queryArg, pageParam }) => {
        return ({
          url: '/multifilter',
          method: 'POST',
          body: { ...queryArg, page: pageParam + 1 }
        })
      },
      transformResponse: (response, meta, { queryArg, pageParam }) => {
        const root = response?.data ? response.data : response;

        const products = root?.products || []; // ✅ Merr vetëm products

        const limit = queryArg.per_page || 40;

        // ✅ Next page logjikë bazuar në numrin e produkteve të kthyer
        const nextPage = products.length === limit ? pageParam + 1 : null;

        return {
          products,
          totalCount: root?.totalCount,
          nextPage
        };
      },
      providesTags: ['BannerProducts']
    })
  })
});

export const { useGetSubcategoryQuery, useGetProductsInfiniteQuery, useGetSearchProductsInfiniteQuery, useGetBannerProductsInfiniteQuery } = productsApi;
